import type { ContactType } from "../types/contact";

export type ProfileId = "colorado" | "california-aps";

export type ContactTypeMeta = {
  value: ContactType;
  label: string;
  icon: string;
  colorClass: string;
  requiredFields: number;
};

// Canonical contact-type order used throughout the frontend.
export const CONTACT_TYPE_ORDER: readonly ContactType[] = [
  "Intake",
  "SafetyCheck",
  "PlacementUpdate",
  "CasePlanReview",
];

const BASE_META: Record<ContactType, ContactTypeMeta> = {
  Intake: {
    value: "Intake",
    label: "Initial Intake",
    icon: "📝",
    colorClass: "bg-red-50 text-red-700",
    requiredFields: 6,
  },
  SafetyCheck: {
    value: "SafetyCheck",
    label: "Safety Check",
    icon: "🏠",
    colorClass: "bg-orange-50 text-orange-700",
    requiredFields: 4,
  },
  PlacementUpdate: {
    value: "PlacementUpdate",
    label: "Placement Update",
    icon: "🔄",
    colorClass: "bg-blue-50 text-blue-700",
    requiredFields: 5,
  },
  CasePlanReview: {
    value: "CasePlanReview",
    label: "Case Plan Review",
    icon: "📋",
    colorClass: "bg-purple-50 text-purple-700",
    requiredFields: 4,
  },
};

const APS_OVERRIDES: Partial<Record<ContactType, Partial<ContactTypeMeta>>> = {
  Intake: {
    label: "Report Intake",
    icon: "📝",
    colorClass: "bg-blue-50 text-blue-700",
  },
  SafetyCheck: {
    label: "Client Visit & Assessment",
    icon: "🏠",
    colorClass: "bg-teal-50 text-teal-700",
  },
  PlacementUpdate: {
    label: "Collateral Contact",
    icon: "📞",
    colorClass: "bg-amber-50 text-amber-700",
  },
  CasePlanReview: {
    label: "Service Plan Follow-up",
    icon: "🗂️",
    colorClass: "bg-purple-50 text-purple-700",
  },
};

export const PROFILE_IDS: readonly ProfileId[] = ["colorado", "california-aps"];

export function isValidProfileId(value: string | null | undefined): value is ProfileId {
  return value === "colorado" || value === "california-aps";
}

export function normalizeProfileId(value: string | null | undefined): ProfileId | null {
  const v = String(value || "").trim().toLowerCase();
  return isValidProfileId(v) ? v : null;
}

export function getContactTypeMetasForProfile(profileId: string | null | undefined): ContactTypeMeta[] {
  const prof = normalizeProfileId(profileId) ?? "colorado";
  const isAps = prof === "california-aps";
  return CONTACT_TYPE_ORDER.map((value) => {
    const base = BASE_META[value] ?? {
      value,
      label: value,
      icon: "🔖",
      colorClass: "bg-gray-50 text-gray-700",
      requiredFields: 5,
    };
    if (!isAps) return base;
    const override = APS_OVERRIDES[value];
    return override ? { ...base, ...override } : base;
  });
}
